import { ApolloClient, InMemoryCache, from, split } from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { HttpLink } from '@apollo/client/link/http';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';

import { filterNullish } from '@modernhealth/filter-nullish';
import { Conversation, Message } from './types';
import {
    ConversationsDocument,
    ConversationsQuery,
    ConversationsQueryVariables,
} from './documents/conversationsQuery.generated';
import {
    GetMessagesDocument,
    GetMessagesQuery,
    GetMessagesQueryVariables,
} from './documents/getMessagesQuery.generated';
import {
    OnNewMessageDocument,
    OnNewMessageSubscription,
} from './documents/onNewMessageSubscription.generated';
import {
    OnMessageUpdateDocument,
    OnMessageUpdateSubscription,
} from './documents/onMessageUpdateSubscription.generated';
import {
    NewMessageDocument,
    NewMessageMutation,
    NewMessageMutationVariables,
} from './documents/newMessageMutation.generated';
import {
    UpdateMessageDocument,
    UpdateMessageMutation,
    UpdateMessageMutationVariables,
} from './documents/updateMessageMutation.generated';
import {
    OnNewConversationDocument,
    OnNewConversationSubscription,
} from './documents/onNewConversationSubscription.generated';
import { ActionType, Action, State, reducer, initialState, Selectors } from './state';
import { createStore } from './createStore';
import { createObserver } from './createObserver';
import {
    createOnNewMessage,
    createOnMessageUpdate,
    createOnNewConversation,
} from './createEventHandlers';
import { ErrorCode } from './RequestState';
import { isWebSocketSubscription } from './apollo';

interface Subscription {
    closed: boolean;
    unsubscribe(): void;
}

interface GetMessagesOptions {
    after?: string;
    count?: number;
}

export interface MessagingClient {
    getConversations(): Promise<readonly Conversation[]>;
    getMessages(
        conversationUuid: string,
        options?: GetMessagesOptions,
    ): Promise<readonly Message[]>;
    sendMessage(conversationUuid: string, content: string): Promise<void>;
    sendMessageReadReceipt(message: Message): Promise<void>;

    startWebSocketSubscriptions(): void;

    setActiveUserUuid(uuid: string | null): void;

    // state management
    subscribe(listener: () => void): () => void;
    getState(): State;
    selectors: typeof Selectors;
}

export function createMessagingClient({
    httpURI,
    webSocketURI,
    getAuthenticationData,
    onError,
}: {
    httpURI: string;
    webSocketURI: string;
    getAuthenticationData(): Promise<{ tokenType: string; authToken: string } | null>;
    onError(error: unknown): void;
}): MessagingClient {
    const store = createStore<State, Action>(reducer, initialState);

    const httpLink = from([
        new RetryLink({
            attempts: {
                max: 2,
            },
        }),
        setContext(async (_, { options }) => {
            const headers = { ...options };
            const authData = await getAuthenticationData();

            if (authData) {
                headers.Authorization = `${authData.tokenType} ${authData.authToken}`;
            }

            return { headers };
        }),
        new HttpLink({
            uri: httpURI,
        }),
    ]);

    const webSocketLink = new WebSocketLink({
        uri: webSocketURI,
        options: {
            lazy: true,
            reconnect: true,
            connectionParams: async () => {
                const authData = await getAuthenticationData();

                return {
                    headers: {
                        token: authData ? authData.authToken : '',
                    },
                };
            },
        },
    });

    const link = split(isWebSocketSubscription, webSocketLink, httpLink);
    const cache = new InMemoryCache();

    const apolloClient = new ApolloClient({
        link,
        cache,
        // TODO: ONECARE-445 add new name to config for access control
        // `access-control-request-headers: apollographql-client-name`
        // name: 'messaging-kit',
    });

    const initializedWebSocketSubscribers: Subscription[] = [];
    const uninitializedWebSocketSubscribers: (() => Subscription)[] = [
        createObserver<OnNewMessageSubscription>(
            apolloClient,
            OnNewMessageDocument,
            createOnNewMessage(store),
            onError,
        ),
        createObserver<OnMessageUpdateSubscription>(
            apolloClient,
            OnMessageUpdateDocument,
            createOnMessageUpdate(store),
            onError,
        ),
        createObserver<OnNewConversationSubscription>(
            apolloClient,
            OnNewConversationDocument,
            createOnNewConversation(store),
            onError,
        ),
    ];

    return {
        setActiveUserUuid(uuid) {
            store.dispatch({
                type: ActionType.SET_USER,
                payload: uuid
                    ? {
                          uuid,
                      }
                    : null,
            });
        },
        async getConversations() {
            const { data } = await apolloClient.query<
                ConversationsQuery,
                ConversationsQueryVariables
            >({
                query: ConversationsDocument,
                variables: {
                    active: true,
                },
                fetchPolicy: 'network-only',
            });

            if (data?.conversations?.edges) {
                const convos = filterNullish(data.conversations.edges.map(e => e?.node));

                convos.forEach(c => {
                    if (!c.newestMessage) {
                        return;
                    }

                    store.dispatch({
                        type: ActionType.NEW_MESSAGE,
                        payload: {
                            ...c.newestMessage,
                            conversationUuid: c.uuid,
                        },
                    });
                });

                store.dispatch({ type: ActionType.SET_CONVERSATIONS, payload: convos });
                return convos;
            }

            return [];
        },

        async getMessages(
            conversationUuid: string,
            { after, count = 10 }: GetMessagesOptions = {},
        ) {
            // If this is the initial request for messages, include the
            // conversation data to validate that the conversation
            // exists in addition to the data being required to render
            // chat participant information such as names and avatars
            // whose data is not present in the message nodes
            // themselves.
            const includeConversation = !after;
            const { data } = await apolloClient.query<GetMessagesQuery, GetMessagesQueryVariables>({
                query: GetMessagesDocument,
                variables: {
                    conversationUuid,
                    active: true,
                    after,
                    count,
                    includeConversation,
                },
                fetchPolicy: 'network-only',
            });

            if (includeConversation) {
                if (!data?.conversations?.edges?.[0]?.node) {
                    throw new Error(ErrorCode.NOT_FOUND);
                }

                store.dispatch({
                    type: ActionType.SET_CONVERSATION,
                    payload: data.conversations.edges[0].node,
                });
            }

            if (data?.getMessages?.pageInfo?.oldestMessageUuid) {
                store.dispatch({
                    type: ActionType.SET_OLDEST_MESSAGE,
                    payload: {
                        conversationUuid,
                        oldestMessageUuid: data.getMessages.pageInfo.oldestMessageUuid,
                    },
                });
            }

            if (data?.getMessages?.messages) {
                const messages = filterNullish(data.getMessages.messages);
                store.dispatch({
                    type: ActionType.ADD_MESSAGES,
                    payload: {
                        conversationUuid,
                        messages,
                    },
                });
                return messages;
            }

            return [];
        },

        async sendMessage(conversationUuid: string, content: string) {
            const { data } = await apolloClient.mutate<
                NewMessageMutation,
                NewMessageMutationVariables
            >({
                mutation: NewMessageDocument,
                variables: {
                    conversationUuid,
                    content,
                },
                fetchPolicy: 'network-only',
            });

            if (!data?.newMessage?.ok || !data?.newMessage?.message) {
                throw new Error(ErrorCode.FAILURE);
            }

            store.dispatch({
                type: ActionType.NEW_MESSAGE,
                payload: data.newMessage.message,
            });
        },

        async sendMessageReadReceipt(message: Message) {
            const { data } = await apolloClient.mutate<
                UpdateMessageMutation,
                UpdateMessageMutationVariables
            >({
                mutation: UpdateMessageDocument,
                variables: {
                    conversationUuid: message.conversationUuid,
                    messageUuid: message.uuid,
                    readAt: new Date(),
                },
                fetchPolicy: 'network-only',
            });

            if (!data?.updateMessage?.ok) {
                throw new Error(ErrorCode.FAILURE);
            }
        },

        startWebSocketSubscriptions() {
            while (uninitializedWebSocketSubscribers.length > 0) {
                const subscribe = uninitializedWebSocketSubscribers.pop();
                if (subscribe) {
                    initializedWebSocketSubscribers.push(subscribe());
                }
            }
        },

        // state management
        subscribe: store.subscribe,
        getState: store.getState,
        selectors: Selectors,
    };
}
